import React, { useMemo } from "react";

const datePart = (value) => String(value || "").slice(0, 10);

const formatDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart(value));
  if (!match) return datePart(value) || "Date unavailable";
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatTime = (value) => {
  const match = /[ T](\d{2}):(\d{2})/.exec(String(value || ""));
  if (!match) return "";
  const hour = Number(match[1]);
  const minute = match[2];
  return `${hour % 12 || 12}:${minute} ${hour >= 12 ? "PM" : "AM"}`;
};

const appointmentAddress = (appointment) => {
  const locality = [
    appointment.service_city,
    appointment.service_state,
    appointment.service_zip,
  ].map((value) => String(value || "").trim()).filter(Boolean).join(" ");
  return [String(appointment.service_street || "").trim(), locality].filter(Boolean).join(", ");
};

const appointmentNotes = (appointment) => [
  appointment.description,
  appointment.details,
  appointment.notes,
].map((value) => String(value || "").trim()).filter((value, index, values) => value && values.indexOf(value) === index);

const CalendarPrintSchedule = ({ appointments, businessName, rangeLabel }) => {
  const groupedAppointments = useMemo(() => {
    const sorted = [...(appointments || [])].sort((left, right) => {
      const startCompare = String(left.start_time || "").localeCompare(String(right.start_time || ""));
      if (startCompare) return startCompare;
      const endCompare = String(left.end_time || "").localeCompare(String(right.end_time || ""));
      if (endCompare) return endCompare;
      return Number(left.id || 0) - Number(right.id || 0);
    });
    return sorted.reduce((groups, appointment) => {
      const key = datePart(appointment.start_time) || "unknown";
      if (!groups[key]) groups[key] = [];
      groups[key].push(appointment);
      return groups;
    }, {});
  }, [appointments]);

  const groups = Object.entries(groupedAppointments);

  return (
    <div className="jf-calendar-print-root" style={{ display: "none" }}>
      <style>{`
        @media print {
          .jf-calendar-print-root {
            display: block !important;
            color: #000 !important;
            background: #fff !important;
            font-family: Arial, sans-serif !important;
            font-size: 11pt !important;
            line-height: 1.35 !important;
          }
          .jf-calendar-print-date { break-after: avoid-page; page-break-after: avoid; }
          .jf-calendar-print-appointment { break-inside: avoid-page; page-break-inside: avoid; }
          @page { margin: 0.55in; }
        }
      `}</style>
      <header style={{ borderBottom: "3px solid #000", paddingBottom: 10, marginBottom: 18 }}>
        <div style={{ fontSize: 20, fontWeight: 900 }}>{businessName || "MyTool NOW"}</div>
        <div style={{ fontSize: 17, fontWeight: 900 }}>APPOINTMENT SCHEDULE</div>
        <div style={{ marginTop: 4, fontWeight: 700 }}>{rangeLabel}</div>
      </header>

      {groups.length === 0 ? (
        <div style={{ padding: "18px 0", fontWeight: 700 }}>No appointments scheduled for this date range.</div>
      ) : groups.map(([date, rows]) => (
        <section key={date} style={{ marginBottom: 20 }}>
          <h2 className="jf-calendar-print-date" style={{ margin: "0 0 8px", paddingBottom: 5, borderBottom: "2px solid #000", fontSize: 15 }}>
            {formatDate(date)}
          </h2>
          {rows.map((appointment) => {
            const startDate = datePart(appointment.start_time);
            const endDate = datePart(appointment.end_time);
            const multiDay = startDate && endDate && startDate !== endDate;
            const address = appointmentAddress(appointment);
            const notes = appointmentNotes(appointment);
            return (
              <article key={appointment.id} className="jf-calendar-print-appointment" style={{ border: "1.5px solid #000", padding: 10, marginBottom: 9 }}>
                <div style={{ fontWeight: 900 }}>
                  {multiDay
                    ? `${formatDate(startDate)}, ${formatTime(appointment.start_time)} — ${formatDate(endDate)}, ${formatTime(appointment.end_time)}`
                    : `${formatTime(appointment.start_time)} — ${formatTime(appointment.end_time)}`}
                </div>
                <div style={{ marginTop: 3, fontSize: 13, fontWeight: 900, overflowWrap: "anywhere" }}>
                  {String(appointment.customer_name || "").trim() || "Customer not specified"}
                </div>
                <div style={{ marginTop: 2, fontWeight: 700, overflowWrap: "anywhere" }}>
                  {String(appointment.title || appointment.job_type_name || "Appointment").trim()}
                </div>
                {String(appointment.employee_name || "").trim() ? (
                  <div style={{ marginTop: 4 }}><strong>Assigned:</strong> {String(appointment.employee_name).trim()}</div>
                ) : null}
                {address ? <div style={{ marginTop: 4, overflowWrap: "anywhere" }}><strong>Service address:</strong> {address}</div> : null}
                {notes.map((note, index) => (
                  <div key={`${appointment.id}-note-${index}`} style={{ marginTop: 5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{note}</div>
                ))}
              </article>
            );
          })}
        </section>
      ))}
    </div>
  );
};

export default CalendarPrintSchedule;
